import { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';

export const useMCPSleep = () => {
  const { user } = useAuth();
  const [sleepRecords, setSleepRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Call MCP server
  const callMCPServer = async (command, params = []) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated to use MCP commands');
    }
    
    try {
      const response = await fetch(API_ENDPOINTS.MCP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          command: `${command} ${params.join(' ')}`.trim(),
          userId: user.uid
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`MCP Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('MCP Server error:', error);
      throw error;
    }
  };

  // Load sleep records from MCP server
  const loadSleepRecords = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await callMCPServer('list_sleep');
      if (result.status === 'success') {
        setSleepRecords(result.sleep || []);
      } else {
        throw new Error(result.message || 'Failed to load sleep records');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading sleep records:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Add sleep record via MCP server
  const addSleepRecord = async (sleepData) => {
    if (!user) return null;
    
    try {
      const result = await callMCPServer('add_sleep', [sleepData.hours.toString()]);
      if (result.status === 'success') {
        await loadSleepRecords();
        return result.sleep;
      } else {
        throw new Error(result.message || 'Failed to add sleep record');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error adding sleep record:', err);
      throw err;
    }
  };

  // Update sleep record via MCP server
  const updateSleepRecord = async (id, updates) => {
    if (!user) return null;
    
    try {
      const updateString = Object.entries(updates)
        .map(([key, value]) => `${key}:${value}`)
        .join(' ');
      
      const result = await callMCPServer('update_sleep', [id, updateString]);
      
      if (result.status === 'success') {
        await loadSleepRecords();
        return result.sleep;
      } else {
        throw new Error(result.message || 'Failed to update sleep record');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating sleep record:', err);
      throw err;
    }
  };

  // Delete sleep record via MCP server
  const deleteSleepRecord = async (id) => {
    if (!user) return;
    
    try {
      const result = await callMCPServer('delete_sleep', [id]);
      if (result.status === 'success') {
        await loadSleepRecords();
      } else {
        throw new Error(result.message || 'Failed to delete sleep record');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting sleep record:', err);
      throw err;
    }
  };

  // Load sleep records when user changes
  useEffect(() => {
    if (user) {
      loadSleepRecords();
    } else {
      setSleepRecords([]);
    }
  }, [user, loadSleepRecords]);

  return {
    sleepRecords,
    loading,
    error,
    addSleepRecord,
    updateSleepRecord,
    deleteSleepRecord,
    loadSleepRecords,
    setError
  };
};
